import { lazy } from 'react';
import { authRoles } from 'app/auth/authRoles';
import { Navigate } from 'react-router-dom';
import AuthGuard from 'app/auth/AuthGuard';
import Loadable from 'app/components/Loadable';
import { VerifyProvider } from './contexts/VerifyContext';
import { UploadProvider } from './contexts/UploadContext';
import { ApproverProvider } from './contexts/ApproverContext';
import { AuthProvider } from './contexts/JWTAuthContext';
import { LetterProvider } from './contexts/LetterContext';
import { AccountProvider } from './contexts/AccountContext';
import { StampProvider } from './contexts/StampContext';

import NotFound from 'app/views/sessions/NotFound';
import JwtLogin from './views/sessions/JwtLogin';
import PageViewer from './views/docs/PageViewer';
import PageVerifier from './views/outside/PageVerifier';
import QrViewer from './views/outside/QrViewer';
import RouteAccess from 'app/components/RouteAccess';
import sessionRoutes from 'app/views/sessions/SessionRoutes';
import QRLetterViewer from './views/outside/QrLetterViewer';
import Profile from './views/dashboard/Profile';


const Dashboard = Loadable(lazy(() => import('app/views/dashboard/Dashboard')));

const MasterFields = Loadable(lazy(() => import('app/views/master/MasterFields')));
const MasterOptions = Loadable(lazy(() => import('app/views/master/MasterOptions')));
const MasterTemplates = Loadable(lazy(() => import('app/views/master/MasterTemplates')));
const MasterGroup = Loadable(lazy(() => import('app/views/master/MasterGroup')));
const MasterAuthorization = Loadable(lazy(() => import('app/views/master/MasterAuthorization')));
const MasterDepartment = Loadable(lazy(() => import('app/views/master/MasterDepartment')));
const AccountEditable = Loadable(lazy(() =>  import('app/components/Form/Account/AccountEditable')));

const Tracks = Loadable(lazy(() => import('app/views/tracking/Tracks')));
const Tasks = Loadable(lazy(() => import('app/views/tracking/Tasks')));
const Checks = Loadable(lazy(() => import('app/views/tracking/Checks')));
const ReviewTrack = Loadable(lazy(() => import('app/views/tracking/ReviewTrack')));
const ReviewTrackDocument = Loadable(lazy(() => import('app/views/tracking/ReviewTrackDocument')));
const Approvers = Loadable(lazy(() => import('app/views/tracking/Approvers')));
const ApproverReview = Loadable(lazy(() => import('app/views/tracking/ApproverReview')));
const Cancellation = Loadable(lazy(() => import('app/views/tracking/Cancellation')));

const Reports = Loadable(lazy(() => import('app/views/report/Reports')));
const Monitoring = Loadable(lazy(() => import('app/views/report/Monitoring')));

const CommunicationItems = Loadable(lazy(() => import('app/views/surkom/CommunicationItems')));
const CommunicationReviews = Loadable(lazy(() => import('app/views/surkom/CommunicationReviews')));
const FormLetter = Loadable(lazy(() => import('app/components/Form/Letter/FormLetter')));
const FormEditableLetter = Loadable(lazy(() => import('app/components/Form/Letter/FormEditableLetter')));
const ViewCommunication = Loadable(lazy(() => import('app/views/surkom/ViewCommunication')));  
const LetterPageVerifier = Loadable(lazy(() => import('app/views/outside/LetterPageVerifier')));
const ReportCommunication = Loadable(lazy(() => import('app/views/surkom/ReportCommunication')) )

const StampItems = Loadable(lazy(() => import('app/views/docstamp/StampItems')));
const FormStamp = Loadable(lazy(() => import('app/components/Form/Stamp/FormStamp')));
const ViewStamp = Loadable(lazy(() => import('app/views/docstamp/StampViews')));
const PdfViewerPage = Loadable(lazy(() => import('app/views/docstamp/PdfViewerPage')));

const ManualBook = Loadable(lazy(() => import('app/views/docs/ManualBook')));

export default function generateRoutes() {
  return [
    {
      element: (
        <AuthGuard />
      ),
      children: [
        { path: '/session/signin', element: <JwtLogin /> },
        { path: '/dashboard', element: <Dashboard />, auth: authRoles.admin },
        { path: '/dashboard/account/profile', element: <AccountProvider><Profile /></AccountProvider>, auth: authRoles.admin },
        { key : 18, path: '/master/authorization-rules', element: <RouteAccess keyId={18} > <MasterAuthorization /> </RouteAccess> },
        { key : 22, path: '/master/mapping-departs', element: <RouteAccess keyId={22} > <MasterDepartment /> </RouteAccess> },
        { key : 4, path: '/master/options', element: <RouteAccess keyId={4} > <MasterOptions /> </RouteAccess> },
        { key : 5, path: '/master/fields', element: <RouteAccess keyId={5} > <MasterFields /></RouteAccess> },
        { key : 6, path: '/master/templates', element:  <RouteAccess keyId={6} > <MasterTemplates /></RouteAccess>},
        { key : 7, path: '/master/grouptemplate', element: <RouteAccess keyId={7} > <MasterGroup /> </RouteAccess> },

        { key : 8, path: '/tracking/tracks', element: <RouteAccess keyId={8} > <Tracks /> </RouteAccess> },
        { key : 9, path: '/tracking/tasks', element: <RouteAccess keyId={9} > <Tasks /> </RouteAccess> },
        { key : 10, path: '/tracking/checks', element: <RouteAccess keyId={10} > <Checks /> </RouteAccess> },
        { key : 11, path: '/tracking/approvers', element: <RouteAccess keyId={11} > <Approvers /> </RouteAccess> },
        { key : 12, path: '/tracking/cancellation', element: <RouteAccess keyId={12} > <Cancellation /></RouteAccess> },

        { path: '/tracking/tasks/document', element: <ReviewTrackDocument />, auth: authRoles.admin },
        { path: '/tracking/checks/document', element: <ReviewTrackDocument />, auth: authRoles.admin },
        { path: '/tracking/tracks/document', element: <ReviewTrackDocument />, auth: authRoles.admin },
        { path: '/tracking/tasks/review', element: <ReviewTrack />, auth: authRoles.admin },
        { path: '/tracking/checks/review', element: <ReviewTrack />, auth: authRoles.admin },
        { path: '/tracking/tracks/review', element: <ReviewTrack />, auth: authRoles.admin },
        { path: '/tracking/approvers/viewers', element: <ApproverReview />, auth: authRoles.admin },
        { path: '/tracking/tasks/review/document', element: <ReviewTrackDocument />, auth: authRoles.admin },
        { path: '/tracking/checks/review/document', element: <ReviewTrackDocument />, auth: authRoles.admin },
        { path: '/tracking/tracks/review/document', element: <ReviewTrackDocument />, auth: authRoles.admin },
        { path: '/tracking/cancellation/viewers', element: <ApproverReview />, auth: authRoles.admin },
        { path: '/account/employee/editable', element: <AccountProvider><AccountEditable /></AccountProvider>, auth: authRoles.admin },

        { key : 13, path: '/tracking/reports', element: <RouteAccess keyId={13} > <Reports /></RouteAccess>},
        { key : 14, path: '/tracking/monitoring', element: <RouteAccess keyId={14} > <Monitoring /></RouteAccess>},

        { key : 16, path: '/communication/letters/items', element: <RouteAccess keyId={16} > <CommunicationItems /></RouteAccess>},
        { key : 17, path: '/communication/letters/reviews', element: <RouteAccess keyId={17} > <CommunicationReviews /></RouteAccess>},
        { path: '/communication/letters/form', element: <LetterProvider><FormLetter /></LetterProvider>, auth: authRoles.admin },
        { path: '/communication/letters/form/editable', element: <LetterProvider><FormEditableLetter /></LetterProvider>, auth: authRoles.admin },
        { path: '/communication/letters/generate/report', element: <LetterProvider><ReportCommunication /></LetterProvider>, auth: authRoles.admin },
        { path: '/communication/letters/items/views', element: <ViewCommunication />, auth: authRoles.admin },

        { key : 20, path: '/docstamps/stamps/histories', element: <RouteAccess keyId={20} > <StampItems /></RouteAccess>},
        { key : 21, path: '/docstamps/stamps/create', element: <RouteAccess keyId={21} > <StampProvider><FormStamp /></StampProvider></RouteAccess>},
        { key : 21, path: '/docstamps/stamps/edit', element: <RouteAccess keyId={21} > <StampProvider><FormStamp /></StampProvider></RouteAccess>},
        { key : 20, path: '/docstamps/stamps/views', element: <RouteAccess keyId={20} > <StampProvider><ViewStamp /></StampProvider></RouteAccess>},
        { key : 20, path: '/docstamps/stamps/document/view', element: <RouteAccess keyId={20} > <StampProvider><PdfViewerPage /></StampProvider></RouteAccess>},

        { key: 91, path: '/user-guide', element: <ManualBook />, auth: authRoles.admin },
        { key: 92, path: '/user-guide-loa', element: <ManualBook />, auth: authRoles.admin }
      ],
    },
    ...sessionRoutes,
    { path: '/', element: <Navigate to="dashboard" /> },
    { path: '/projects/tracking/documents', element: <PageViewer /> },
    { 
      path: '/project/document/approval',
      element: <AuthProvider>
        <VerifyProvider><ApproverProvider><UploadProvider><PageVerifier /></UploadProvider></ApproverProvider></VerifyProvider>
      </AuthProvider> 
    },
    { 
      path: '/project/communication/approval',
      element: <AuthProvider>
        <VerifyProvider><LetterProvider><LetterPageVerifier/></LetterProvider></VerifyProvider>
      </AuthProvider> 
    },
    { 
      path: '/project/document/viewer',
      element: <AuthProvider>
        <VerifyProvider><QrViewer /></VerifyProvider>
      </AuthProvider> 
    },
    {
      path: '/outside/surkom/:CLIENT_KEY',
      element: <LetterProvider><QRLetterViewer/></LetterProvider>
    },
    { path: '*', element: <NotFound /> },
  ];
}